import { CheckCircleOutlined, CloseOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import { keepPreviousData } from '@tanstack/react-query';
import { Button, Descriptions, Flex, Image, Input, message, Popconfirm, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { BASE_PATH } from '../../..';
import { GenerateODataFilter } from '../../../helpers/odataFunctions';
import { AssetApprovalStatus, AssetApprovalStatusOptions } from '../../../models/enums';
import { useBomModelServiceDeleteBomModel, useBomModelServiceGetBomModel, useBomModelServicePutBomModel, useInternalClientServiceGetInternalClient } from '../../../openapi/queries';
import { BomModelViewDTO } from '../../../openapi/requests';
import { Filter } from '../../../types/types';
import { MetadataSelector } from '../Inputs/MetadataSelector';
import EditBomModel from './EditBomModel';
import { BomDesignerProvider } from '../../../contexts/BomDesignerContext';

const metadateKeys: string[] = ['ean', 'brand', 'banner'];

const ModelList: React.FC = () => {
    const { data: clients } = useInternalClientServiceGetInternalClient({});

    const [messageApi, contextHolder] = message.useMessage();
    const [selectedModel, setSelectedModel] = useState<BomModelViewDTO | undefined>(undefined);
    const [editorOpen, setEditorOpen] = useState(false);

    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebounce(query, 500);
    const [selectedClients, setSelectedClients] = useState<number[]>([]);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
    const [customFilters, setCustomFilters] = useState<Filter[]>([]);

    const filters = useMemo(() => {
        let filters: Filter[] = [];

        if (debouncedQuery) {
            filters.push({ type: 'search', values: [{value: debouncedQuery}], property: 'title,metadata.ean', name: 'title' });
        }

        if (selectedClients.length > 0) {
            filters.push({ type: 'select', values: selectedClients.map(e => ({value: e})), property: 'clientId', name: 'clientId' });
        }

        for (let i = 0; i < customFilters.length; i++) {
            if(customFilters[i].values.length > 0){
                filters.push(customFilters[i]);
            }
        }

        return filters;
    }, [debouncedQuery, selectedClients, customFilters]);

    useEffect(() => {
        setPage(1);
    }, [debouncedQuery, selectedClients, customFilters]);

    const { data, refetch, isFetching } = useBomModelServiceGetBomModel({
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true'
    }, undefined, { refetchOnWindowFocus: false, placeholderData: keepPreviousData  });
    const { mutateAsync: deleteModel } = useBomModelServiceDeleteBomModel();
    const { mutateAsync: editModel } = useBomModelServicePutBomModel();

    const handleCustomFilterAdd = useCallback(() => {
        setCustomFilters(v => [...v, { type: 'select', values: [], property: '', name: '' }]);
    }, []);
    const handleCustomFilterRemove = useCallback((index: number) => {
        setCustomFilters(v => v.filter((e,i) => i !== index));
    }, []);
    const handleCustomFilterChange = useCallback((value: Filter, index: number) => {
        setCustomFilters(v => v.map((e,i) => i === index ? value : e));
    }, []);

    const handleClientChange = useCallback((value: any, option: { label: string; value: string; } | { label: string; value: string; }[]) => {
        if (Array.isArray(option)) {
            setSelectedClients(option.map(e => parseInt(e.value)));
        }
    }, []);

    const handleEditClose = useCallback(() => {
        setSelectedModel(undefined);
        setEditorOpen(false);
        refetch();
    }, [refetch]);

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<BomModelViewDTO> | SorterResult<BomModelViewDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const handleDelete = useCallback((model: BomModelViewDTO) => {
        if (model.id) {
            deleteModel({ key: model.id }).then(() => {
                messageApi.success("Model deleted");
                refetch();
            });
        }
    }, [deleteModel, messageApi, refetch]);

    const handleEnable = useCallback((model: BomModelViewDTO) => {
        if (model.id) {
            editModel({ key: model.id, requestBody: { ...model, isEnabled: true } }).then(() => {
                messageApi.success("Model enabled");
                refetch();
            })
        }
    }, [editModel, messageApi, refetch]);

    const handleDisable = useCallback((model: BomModelViewDTO) => {
        if (model.id) {
            editModel({ key: model.id, requestBody: { ...model, isEnabled: false } }).then(() => {
                messageApi.success("Model disabled");
                refetch();
            })
        }
    }, [editModel, messageApi, refetch]);

    const handleEdit = useCallback((model: BomModelViewDTO) => {
        setSelectedModel(model);
        setEditorOpen(true);
    }, []);

    const handleCreate = useCallback(() => {
        setSelectedModel(undefined);
        setEditorOpen(true);
    }, []);

    const columns: ColumnsType<BomModelViewDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Thumbnail',
            dataIndex: 'thumbnail',
            key: 'title',
            render: (_, value) => (
                <Image
                    preview={false}
                    src={BASE_PATH + value.thumbnail}
                    height={100}
                />)
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
        },
        {
            title: 'Enabled',
            dataIndex: 'enabled',
            key: 'enabled',
            render: (_, value) => (value.isEnabled ? <CheckCircleOutlined style={{ color: '#389e0d' }} /> : <StopOutlined style={{ color: '#cf1322' }} />)
        },
        {
            title: 'Status',
            dataIndex: 'approvalStatus',
            key: 'approvalStatus',
            render: (_, value) => (AssetApprovalStatusOptions[value.approvalStatus as AssetApprovalStatus])
        },
        {
            title: 'Tags',
            dataIndex: 'tags',
            key: 'tags',
            render: (_, { tags }) => (<>{tags?.map((tag) => <Tag key={tag}>{tag}</Tag>)}</>),
        },
        {
            title: 'Metadata',
            dataIndex: 'metadata',
            key: 'metadata',
            width: 200,
            render: (_, { metadata }) => (<><Descriptions size='small' column={1} items={metadata?.filter(e => metadateKeys.includes(e.name.toLowerCase())).map(e => ({ label: e.name, children: e.value }))} /></>),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" onClick={() => handleEdit(record)}>Edit</Button>
                    {record.isEnabled ?
                        <Button type="link" onClick={() => handleDisable(record)}>Disable</Button> :
                        <Button type="link" onClick={() => handleEnable(record)}>Enable</Button>
                    }
                    <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                        <Button type="link">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}

            <Flex justify='space-between' style={{ marginBottom: 16 }}>

                <Space>
                    <Input placeholder='search' value={query} onChange={e => setQuery(e.target.value)} />
                    <Select
                        mode="multiple"
                        placeholder="Client"
                        onChange={handleClientChange}
                        popupMatchSelectWidth={false}
                        size='middle'
                        style={{ minWidth: 100 }}
                        options={clients?.value.map(e => ({ label: e.name, value: e.id.toString() }))}
                    />
                </Space>

                <Space>
                    <Button type="primary" onClick={handleCreate} icon={<PlusOutlined />}>
                        New model
                    </Button>
                </Space>
            </Flex>

            <Space direction='vertical'>
                {customFilters.map((e,i) => <Space key={e.property}><MetadataSelector assetType='bommodel' value={e} onChange={v => handleCustomFilterChange(v, i)} /><Button onClick={() => handleCustomFilterRemove(i)} icon={<CloseOutlined />}></Button></Space>)}
                <Button onClick={handleCustomFilterAdd} icon={<PlusOutlined />}>Filter</Button>
            </Space>

            <BomDesignerProvider odataFilters={[]}>
                <EditBomModel isOpen={editorOpen} onClose={handleEditClose} modelId={selectedModel?.id} />
            </BomDesignerProvider>

            <Table
                loading={isFetching}
                dataSource={data?.value ?? Array.from({ length: pageSize }).map(x => ({}))}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                showSorterTooltip={false}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    showQuickJumper: true,
                    showSizeChanger: true,
                }} />
        </>
    );
};

export default ModelList;